import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiNavigation, FiSave } from 'react-icons/fi';
import { fetchTripByIdRequest, updateTripRequest } from '../../store/actions/tripsActions';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripEdit.css';

const AdminTripEdit = () => {
    const { tripId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedTrip, update } = useSelector((state) => state.trips);
    const [formData, setFormData] = useState({
        category: '',
        name: '',
        combined_addres: '',
        activity_start_date: '',
        activity_end_date: '',
        trip_effective_start_date: '',
        trip_effective_end_date: '',
        new_trip_effective_start_date: '',
        new_trip_effective_end_date: '',
        new_trip_start_date: '',
        new_trip_end_date: '',
        new_trip_price: '',
        activity_price: '',
        distribution_type: 'inclusion',
        reservation_number: '',
        supplier_name: '',
        supplier_address: '',
        supplier_zip_code: '',
        supplier_city: '',
        supplier_country: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Fonction pour convertir DD/MM/YYYY en YYYY-MM-DD
    const convertToAPIFormat = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
        }
        // Si déjà au format YYYY-MM-DD, retourner tel quel
        return dateStr;
    };

    // Fonction pour convertir YYYY-MM-DD en DD/MM/YYYY
    const convertToDisplayFormat = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('-') && !dateStr.includes('/')) {
            const parts = dateStr.split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
        }
        // Si déjà au format DD/MM/YYYY, retourner tel quel
        return dateStr;
    };

    useEffect(() => {
        if (tripId) {
            dispatch(fetchTripByIdRequest(tripId));
        }
    }, [tripId, dispatch]);

    useEffect(() => {
        if (selectedTrip.data) {
            const trip = selectedTrip.data.activity || selectedTrip.data;
            const activity = trip.activity || trip;

            // Formater l'adresse combinée depuis activity_address, zip_code, city, country
            // Format: activity_address, zip_code, city, country (avec pays en minuscule)
            let formattedAddress = '';
            if (activity.combined_addres) {
                formattedAddress = activity.combined_addres;
            } else {
                // Construire depuis les champs individuels
                const addressParts = [];
                const address = activity.activity_address || activity.supplier_address || '';
                const zipCode = activity.activity_zip_code || activity.supplier_zip_code || '';
                const city = activity.activity_city || activity.supplier_city || '';
                const country = (activity.activity_country || activity.supplier_country || '').toLowerCase();

                if (address) addressParts.push(address);
                if (zipCode) addressParts.push(zipCode);
                if (city) addressParts.push(city);
                if (country) addressParts.push(country);

                if (addressParts.length > 0) {
                    formattedAddress = addressParts.join(',');
                }
            }

            setFormData({
                category: activity.category || '',
                name: activity.name || '',
                combined_addres: formattedAddress,
                activity_start_date: convertToDisplayFormat(activity.activity_start_date || activity.trip_effective_start_date || ''),
                activity_end_date: convertToDisplayFormat(activity.activity_end_date || activity.trip_effective_end_date || ''),
                trip_effective_start_date: convertToDisplayFormat(activity.trip_effective_start_date || ''),
                trip_effective_end_date: convertToDisplayFormat(activity.trip_effective_end_date || ''),
                new_trip_effective_start_date: (activity.new_trip_effective_start_date && activity.new_trip_effective_start_date !== null) ? convertToDisplayFormat(activity.new_trip_effective_start_date) : '',
                new_trip_effective_end_date: (activity.new_trip_effective_end_date && activity.new_trip_effective_end_date !== null) ? convertToDisplayFormat(activity.new_trip_effective_end_date) : '',
                new_trip_start_date: convertToDisplayFormat(activity.new_trip_start_date || ''),
                new_trip_end_date: convertToDisplayFormat(activity.new_trip_end_date || ''),
                new_trip_price: activity.new_trip_price ? activity.new_trip_price.toString().replace(',', '.').replace(' EUR', '') : '',
                activity_price: activity.activity_price ? activity.activity_price.toString().replace(',', '.').replace(' EUR', '') : '',
                distribution_type: activity.distribution_type || 'inclusion',
                reservation_number: activity.reservation_number || '',
                supplier_name: activity.supplier_name || '',
                supplier_address: activity.supplier_address || activity.activity_address || '',
                supplier_zip_code: activity.supplier_zip_code || activity.activity_zip_code || '',
                supplier_city: activity.supplier_city || activity.activity_city || '',
                supplier_country: activity.supplier_country || activity.activity_country || '',
            });
        }
        setLoading(selectedTrip.loading);
        setError(selectedTrip.error);
    }, [selectedTrip]);

    useEffect(() => {
        if (update.success) {
            setSuccessMessage('séjourmodifié avec succès !');
            // Ne pas rediriger, juste afficher le message de succès
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
        if (update.error) {
            setError(update.error);
        }
    }, [update.success, update.error]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation des dates
        const startDate = formData.activity_start_date;
        const endDate = formData.activity_end_date;
        if (startDate && endDate) {
            const parseDate = (dateStr) => {
                if (dateStr.includes('/')) {
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                        return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}T00:00:00`);
                    }
                }
                return new Date(dateStr + 'T00:00:00');
            };
            const start = parseDate(startDate);
            const end = parseDate(endDate);
            if (start > end) {
                setError('La date de début doit être inférieure ou égale à la date de fin.');
                return;
            }
        }

        // Préparer les données pour l'API
        // Note: Toutes les dates DOIVENT être au format DD/MM/YYYY pour l'API
        const tripData = {
            category: formData.category || undefined,
            name: formData.name || undefined,
            combined_addres: formData.combined_addres || undefined,
            // Toutes les dates doivent être au format DD/MM/YYYY pour l'API
            activity_start_date: formData.activity_start_date || undefined,
            activity_end_date: formData.activity_end_date || undefined,
            // trip_effective_start_date et trip_effective_end_date DOIVENT être au format DD/MM/YYYY
            trip_effective_start_date: formData.trip_effective_start_date || undefined,
            trip_effective_end_date: formData.trip_effective_end_date || undefined,
            // new_trip_effective_start_date et new_trip_effective_end_date DOIVENT aussi être au format DD/MM/YYYY
            // Ne les inclure QUE s'ils ont une valeur valide au format DD/MM/YYYY
            new_trip_effective_start_date: (formData.new_trip_effective_start_date && formData.new_trip_effective_start_date.trim() !== '' && formData.new_trip_effective_start_date.includes('/')) ? formData.new_trip_effective_start_date : undefined,
            new_trip_effective_end_date: (formData.new_trip_effective_end_date && formData.new_trip_effective_end_date.trim() !== '' && formData.new_trip_effective_end_date.includes('/')) ? formData.new_trip_effective_end_date : undefined,
            // new_trip_start_date et new_trip_end_date DOIVENT être au format DD/MM/YYYY
            new_trip_start_date: formData.new_trip_start_date || undefined,
            new_trip_end_date: formData.new_trip_end_date || undefined,
            new_trip_price: formData.new_trip_price ? formData.new_trip_price.toString() : undefined,
            activity_price: formData.activity_price ? formData.activity_price.toString() : undefined,
            distribution_type: formData.distribution_type || undefined,
            reservation_number: formData.reservation_number || undefined,
            supplier_name: formData.supplier_name || undefined,
            supplier_address: formData.supplier_address || undefined,
            supplier_zip_code: formData.supplier_zip_code || undefined,
            supplier_city: formData.supplier_city || undefined,
            supplier_country: formData.supplier_country || undefined,
        };

        // Supprimer les champs undefined, null ou vides
        // Pour les dates, on supprime aussi si elles sont 'N/A' ou des chaînes vides
        Object.keys(tripData).forEach(key => {
            const value = tripData[key];
            if (value === undefined || value === null || value === '' || value === 'N/A') {
                delete tripData[key];
                return;
            }

            // Validation spécifique pour les dates effectives - doivent être au format DD/MM/YYYY valide
            if (['new_trip_effective_start_date', 'new_trip_effective_end_date'].includes(key)) {
                if (value && typeof value === 'string' && value.trim() !== '') {
                    // Vérifier le format DD/MM/YYYY avec regex
                    const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (!datePattern.test(value.trim())) {
                        // Format invalide, supprimer
                        delete tripData[key];
                        return;
                    }
                    // Vérifier que chaque partie est un nombre valide
                    const dateParts = value.trim().split('/');
                    const day = parseInt(dateParts[0], 10);
                    const month = parseInt(dateParts[1], 10);
                    const year = parseInt(dateParts[2], 10);
                    if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
                        delete tripData[key];
                        return;
                    }
                } else {
                    // Si ce n'est pas une chaîne valide, supprimer
                    delete tripData[key];
                    return;
                }
            }
        });

        dispatch(updateTripRequest(tripId, tripData));
    };

    // Fonction pour formater l'adresse combinée
    const formatCombinedAddress = (addressData) => {
        const addressParts = [];
        const address = addressData.supplier_address || '';
        const zipCode = addressData.supplier_zip_code || '';
        const city = addressData.supplier_city || '';
        const country = (addressData.supplier_country || '').toLowerCase();

        if (address) addressParts.push(address);
        if (zipCode) addressParts.push(zipCode);
        if (city) addressParts.push(city);
        if (country) addressParts.push(country);

        return addressParts.length > 0 ? addressParts.join(',') : '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedData = { ...formData, [name]: value };

        // Mettre à jour automatiquement l'adresse combinée si un des champs d'adresse change
        if (['supplier_address', 'supplier_zip_code', 'supplier_city', 'supplier_country'].includes(name)) {
            updatedData.combined_addres = formatCombinedAddress(updatedData);
        }

        setFormData(updatedData);
        // Effacer l'erreur si l'utilisateur modifie un champ
        if (error) setError(null);
    };

    if (loading && !selectedTrip.data) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Chargement des données du séjour...</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error && !selectedTrip.data) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <Alert variant="danger">{error}</Alert>
                        <Button variant="secondary" onClick={() => navigate('/admin/trips')}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-trip-edit-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiNavigation className="me-2" />
                                Modifier le séjour
                            </h2>
                            <p className="text-muted mb-0">Modifier les informations du séjour</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/trips')}>
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {successMessage && (
                        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Catégorie *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Type de distribution</Form.Label>
                                    <Form.Select
                                        name="distribution_type"
                                        value={formData.distribution_type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="inclusion">Inclusion</option>
                                        <option value="option">Option</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de début *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="activity_start_date"
                                        value={(() => {
                                            if (!formData.activity_start_date) return '';
                                            if (formData.activity_start_date.includes('/')) {
                                                const parts = formData.activity_start_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.activity_start_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'activity_start_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'activity_start_date', value: '' } });
                                            }
                                        }}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de fin *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="activity_end_date"
                                        value={(() => {
                                            if (!formData.activity_end_date) return '';
                                            if (formData.activity_end_date.includes('/')) {
                                                const parts = formData.activity_end_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.activity_end_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'activity_end_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'activity_end_date', value: '' } });
                                            }
                                        }}
                                        min={(() => {
                                            if (formData.activity_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.activity_start_date);
                                            }
                                            return '';
                                        })()}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date effective de début</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="trip_effective_start_date"
                                        value={(() => {
                                            if (!formData.trip_effective_start_date) return '';
                                            if (formData.trip_effective_start_date.includes('/')) {
                                                const parts = formData.trip_effective_start_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.trip_effective_start_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'trip_effective_start_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'trip_effective_start_date', value: '' } });
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date effective de fin</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="trip_effective_end_date"
                                        value={(() => {
                                            if (!formData.trip_effective_end_date) return '';
                                            if (formData.trip_effective_end_date.includes('/')) {
                                                const parts = formData.trip_effective_end_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.trip_effective_end_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'trip_effective_end_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'trip_effective_end_date', value: '' } });
                                            }
                                        }}
                                        min={(() => {
                                            if (formData.trip_effective_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.trip_effective_start_date);
                                            }
                                            return '';
                                        })()}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouvelle date effective de début</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="new_trip_effective_start_date"
                                        value={(() => {
                                            if (!formData.new_trip_effective_start_date) return '';
                                            if (formData.new_trip_effective_start_date.includes('/')) {
                                                const parts = formData.new_trip_effective_start_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.new_trip_effective_start_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'new_trip_effective_start_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'new_trip_effective_start_date', value: '' } });
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouvelle date effective de fin</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="new_trip_effective_end_date"
                                        value={(() => {
                                            if (!formData.new_trip_effective_end_date) return '';
                                            if (formData.new_trip_effective_end_date.includes('/')) {
                                                const parts = formData.new_trip_effective_end_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.new_trip_effective_end_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'new_trip_effective_end_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'new_trip_effective_end_date', value: '' } });
                                            }
                                        }}
                                        min={(() => {
                                            if (formData.new_trip_effective_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.new_trip_effective_start_date);
                                            }
                                            return '';
                                        })()}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prix</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="activity_price"
                                        value={formData.activity_price}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouveau Prix</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="new_trip_price"
                                        value={formData.new_trip_price}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Numéro de réservation</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="reservation_number"
                                        value={formData.reservation_number}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        <h5 className="mb-3">Nouvelles dates de séjour</h5>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouvelle date de début</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="new_trip_start_date"
                                        value={(() => {
                                            if (!formData.new_trip_start_date) return '';
                                            if (formData.new_trip_start_date.includes('/')) {
                                                const parts = formData.new_trip_start_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.new_trip_start_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'new_trip_start_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'new_trip_start_date', value: '' } });
                                            }
                                        }}
                                        min={(() => {
                                            if (formData.activity_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.activity_start_date);
                                            }
                                            return '';
                                        })()}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouvelle date de fin</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="new_trip_end_date"
                                        value={(() => {
                                            if (!formData.new_trip_end_date) return '';
                                            if (formData.new_trip_end_date.includes('/')) {
                                                const parts = formData.new_trip_end_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            return formData.new_trip_end_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                handleInputChange({ target: { name: 'new_trip_end_date', value: `${day}/${month}/${year}` } });
                                            } else {
                                                handleInputChange({ target: { name: 'new_trip_end_date', value: '' } });
                                            }
                                        }}
                                        min={(() => {
                                            if (formData.new_trip_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.new_trip_start_date);
                                            }
                                            return '';
                                        })()}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Adresse combinée</Form.Label>
                            <Form.Control
                                type="text"
                                name="combined_addres"
                                value={formData.combined_addres}
                                onChange={handleInputChange}
                                placeholder="Format: adresse,code_postal,ville,pays (généré automatiquement)"
                                readOnly
                                className="bg-light"
                                title="L'adresse combinée est générée automatiquement à partir des champs du fournisseur"
                            />
                            <Form.Text className="text-muted">
                                Générée automatiquement à partir des champs Adresse, Code postal, Ville et Pays du fournisseur.
                            </Form.Text>
                        </Form.Group>

                        <hr className="my-4" />

                        <h5 className="mb-3">Informations du fournisseur</h5>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom du fournisseur</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_name"
                                        value={formData.supplier_name}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Adresse du fournisseur</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_address"
                                        value={formData.supplier_address}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code postal</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_zip_code"
                                        value={formData.supplier_zip_code}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ville</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_city"
                                        value={formData.supplier_city}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Pays</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_country"
                                        value={formData.supplier_country}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="secondary" onClick={() => navigate('/admin/trips')} disabled={update.loading}>
                                Annuler
                            </Button>
                            <Button variant="primary" type="submit" disabled={update.loading}>
                                {update.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Modification...
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="me-2" />
                                        Enregistrer
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripEdit;

